import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Tarif } from "../../../settings/tarif";
import { StepNavigation } from "../navigation";
import { ConfirmSubmitDialog } from "../confirm-submit-dialog";
import { completeTutorProfile, tutorLastStep } from "../../../../store/profile/profileActions";
import { errorToast } from "../../../../utils";

const TutorPricing = ({ setCurrentStep }) => {
    const dispatch = useDispatch();
    const methods = useFormContext();
    const pricesInitialized = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { errors: formErrors } = methods.formState;

    const teach = methods.watch("teach");

    useEffect(() => {
        if (!teach?.length) return;

        const existing = methods.getValues("prices") || [];
        const prices = teach.map((v) => {
            const prev = existing.find((p) => p.abbr === v.lang);
            return {
                abbr: v.lang,
                price: prev?.price ?? "",
                priceFive: prev?.priceFive ?? "",
                priceTen: prev?.priceTen ?? "",
            };
        });

        // Re-sync when teach languages change; skip identical first paint after init
        const sameShape =
            pricesInitialized.current &&
            existing.length === prices.length &&
            existing.every((p, i) => p.abbr === prices[i].abbr);

        if (!sameShape) {
            methods.setValue("prices", prices, { shouldDirty: false });
            pricesInitialized.current = true;
        }
    }, [teach, methods]);

    const validateAndConfirm = async () => {
        const prices = methods.getValues("prices") || [];
        if (!prices.length) {
            methods.setError("prices", {
                type: "manual",
                message: "No teaching language found. Please complete your tutor languages first.",
            });
            return;
        }

        const priceFields = prices.flatMap((_, index) => ([
            `prices.${index}.price`,
            `prices.${index}.priceFive`,
            `prices.${index}.priceTen`,
        ]));

        const isValid = await methods.trigger(priceFields);
        if (isValid) {
            setConfirmOpen(true);
        }
    };

    const submitForReview = async () => {
        setIsLoading(true);
        try {
            const days = methods.getValues("days");
            const prices = methods.getValues("prices");

            const lastStepResult = await dispatch(tutorLastStep({ days, prices })).unwrap();
            if (lastStepResult?.existAppointments) {
                errorToast("Please resolve schedule conflicts before submitting.");
                setConfirmOpen(false);
                return;
            }

            await dispatch(completeTutorProfile()).unwrap();
            setConfirmOpen(false);
            setCurrentStep(10);
        } catch {
            // errors handled by thunk / global handlers
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="flex flex-col">
                <h2 className="mb-2 text-xl text-nt-blue">Pricing</h2>
                <p className="text-nt-grey text-sm mb-4">
                    Set lesson prices for each language you teach. You can update these later in settings.
                </p>

                {formErrors.prices?.message ? (
                    <p className="text-nt-red text-sm mb-4">{formErrors.prices.message}</p>
                ) : null}

                <div className="rounded-xl border border-nt-grey-light p-4 md:p-6 mb-4">
                    <Tarif />
                </div>

                <StepNavigation
                    moveBack={() => setCurrentStep(5)}
                    moveForward={validateAndConfirm}
                    isLoading={isLoading}
                />
            </div>

            <ConfirmSubmitDialog
                open={confirmOpen}
                onClose={() => {
                    if (!isLoading) {
                        setConfirmOpen(false);
                    }
                }}
                onConfirm={submitForReview}
                isLoading={isLoading}
                description="Your profile, availability, and pricing will be sent for review. Editing is locked until approved."
            />
        </>
    );
};

export { TutorPricing };
